/**
 * Settings Hooks
 *
 * React hooks for managing settings including tracked products
 * and Gmail integration.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getTrackedProducts,
  searchProducts,
  addTrackedProduct,
  updateTrackedProductType,
  removeTrackedProduct,
  getGmailStatus,
  getGmailAuthUrl,
  completeGmailAuth,
  disconnectGmail,
  getEmailSenderPatterns,
  addEmailSenderPattern,
  removeEmailSenderPattern,
  getGmailAccounts,
  getGmailAccountAuthUrl,
  completeGmailAccountAuth,
  disconnectGmailAccount,
  updateGmailAccount,
  getProductionFloorStatus,
  setProductionFloorPin,
  setProductionFloorEnabled,
  getThreadColors,
  updateThreadColors,
  getQAChecklistConfig,
  updateQAChecklist,
  setQAFloorEnabled,
  getSizeSelectionCollections,
  updateSizeSelectionCollections,
  searchCollections,
  getPatchCollection,
  setPatchCollection,
  type SizeSelectionCollection,
  type PatchCollectionConfig,
  type ProductionFloorStatus,
  type ThreadColor,
  type QAChecklistItem,
  type QAChecklistConfig,
} from '../api';
import type {
  TrackedProduct,
  GmailConnectionStatus,
  EmailSenderPattern,
  GmailAccount,
  ShopifyProduct,
} from '../types';
import { ApiClientError } from '../api';

// ============================================
// Tracked Products Hooks
// ============================================

interface UseTrackedProductsResult {
  data: TrackedProduct[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addProduct: (product: {
    shopifyProductId: string;
    shopifyProductTitle: string;
    shopifyProductHandle: string;
    productType: 'embroidery' | 'patch' | 'both' | 'undetermined';
    priceRange?: { min: string; max: string; currencyCode: string };
  }) => Promise<boolean>;
  updateProductType: (productId: string, productType: 'embroidery' | 'patch' | 'both' | 'undetermined') => Promise<boolean>;
  removeProduct: (productId: string) => Promise<boolean>;
  actionLoading: boolean;
}

export function useTrackedProducts(): UseTrackedProductsResult {
  const [data, setData] = useState<TrackedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTrackedProducts();
      setData(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch tracked products';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addProduct = useCallback(
    async (product: {
      shopifyProductId: string;
      shopifyProductTitle: string;
      shopifyProductHandle: string;
      productType: 'embroidery' | 'patch' | 'both' | 'undetermined';
      priceRange?: { min: string; max: string; currencyCode: string };
    }): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newProduct = await addTrackedProduct(product);
        setData((prev) => [...prev, newProduct]);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to add product';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const updateProductType = useCallback(
    async (productId: string, productType: 'embroidery' | 'patch' | 'both' | 'undetermined'): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await updateTrackedProductType(productId, productType);
        setData((prev) =>
          prev.map((p) =>
            p.shopifyProductId === productId ? { ...p, productType } : p
          )
        );
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update product type';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const removeProduct = useCallback(
    async (productId: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await removeTrackedProduct(productId);
        setData((prev) => prev.filter((p) => p.shopifyProductId !== productId));
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove product';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    addProduct,
    updateProductType,
    removeProduct,
    actionLoading,
  };
}

// ============================================
// Product Search Hook
// ============================================

interface UseProductSearchResult {
  results: ShopifyProduct[];
  loading: boolean;
  error: string | null;
  search: (query: string) => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  clear: () => void;
}

export function useProductSearch(collectionHandle?: string): UseProductSearchResult {
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [currentQuery, setCurrentQuery] = useState('');

  const search = useCallback(async (query: string): Promise<void> => {
    if (!query.trim() && !collectionHandle) {
      setResults([]);
      setHasMore(false);
      setCursor(undefined);
      return;
    }

    setLoading(true);
    setError(null);
    setCurrentQuery(query);
    try {
      const response = await searchProducts(query, 25, undefined, collectionHandle);
      setResults(response.products);
      setHasMore(response.hasNextPage);
      setCursor(response.endCursor);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Search failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [collectionHandle]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!hasMore || loading || !cursor) return;

    setLoading(true);
    try {
      const response = await searchProducts(currentQuery, 25, cursor, collectionHandle);
      setResults((prev) => [...prev, ...response.products]);
      setHasMore(response.hasNextPage);
      setCursor(response.endCursor);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to load more';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, cursor, currentQuery, collectionHandle]);

  const clear = useCallback(() => {
    setResults([]);
    setHasMore(false);
    setCursor(undefined);
    setCurrentQuery('');
  }, []);

  return {
    results,
    loading,
    error,
    search,
    hasMore,
    loadMore,
    clear,
  };
}

// ============================================
// Patch Collection Configuration Hook
// ============================================

interface UsePatchCollectionResult {
  collection: PatchCollectionConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  update: (collection: PatchCollectionConfig | null) => Promise<boolean>;
  actionLoading: boolean;
}

export function usePatchCollection(): UsePatchCollectionResult {
  const [collection, setCollection] = useState<PatchCollectionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPatchCollection();
      setCollection(result);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch patch collection';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const update = useCallback(
    async (newCollection: PatchCollectionConfig | null): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await setPatchCollection(newCollection);
        setCollection(newCollection);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update patch collection';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    collection,
    loading,
    error,
    refetch: fetchData,
    update,
    actionLoading,
  };
}

// ============================================
// Gmail Integration Hooks
// ============================================

interface UseGmailConnectionResult {
  status: GmailConnectionStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<boolean>;
  actionLoading: boolean;
}

const handleError = (setError: React.Dispatch<React.SetStateAction<string | null>>, err: unknown, defaultMessage: string) => {
  const message = err instanceof ApiClientError ? err.message : defaultMessage;
  setError(message);
};

export function useGmailConnection(): UseGmailConnectionResult {
  const [status, setStatus] = useState<GmailConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getGmailStatus();
      setStatus(response);
    } catch (err) {
      handleError(setError, err, 'Failed to get Gmail status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const connect = useCallback(async (): Promise<void> => {
    setActionLoading(true);
    setError(null);
    try {
      const { authUrl } = await getGmailAuthUrl();
      // Open Gmail OAuth in a new window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'gmail-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth callback
      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type === 'gmail-auth-callback' && event.data?.code) {
          window.removeEventListener('message', handleMessage);
          popup?.close();

          try {
            await completeGmailAuth(event.data.code);
            await fetchStatus();
          } catch (err) {
            handleError(setError, err, 'Failed to complete Gmail auth');
          }
        }
      };

      window.addEventListener('message', handleMessage);
    } catch (err) {
      handleError(setError, err, 'Failed to initiate Gmail connection');
    } finally {
      setActionLoading(false);
    }
  }, [fetchStatus]);

  const disconnect = useCallback(async (): Promise<boolean> => {
    setActionLoading(true);
    setError(null);
    try {
      await disconnectGmail();
      setStatus({ isConnected: false });
      return true;
    } catch (err) {
      handleError(setError, err, 'Failed to disconnect Gmail');
      return false;
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    connect,
    disconnect,
    actionLoading,
  };
}

// ============================================
// Email Sender Patterns Hook
// ============================================

interface UseEmailSenderPatternsResult {
  data: EmailSenderPattern[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addPattern: (pattern: { email: string; name: string }) => Promise<boolean>;
  removePattern: (patternId: string) => Promise<boolean>;
  actionLoading: boolean;
}

export function useEmailSenderPatterns(): UseEmailSenderPatternsResult {
  const [data, setData] = useState<EmailSenderPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEmailSenderPatterns();
      setData(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch email patterns';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addPattern = useCallback(
    async (pattern: { email: string; name: string }): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newPattern = await addEmailSenderPattern(pattern);
        setData((prev) => [...prev, newPattern]);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to add pattern';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const removePattern = useCallback(
    async (patternId: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await removeEmailSenderPattern(patternId);
        setData((prev) => prev.filter((p) => p.id !== patternId));
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove pattern';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    addPattern,
    removePattern,
    actionLoading,
  };
}

// ============================================
// Production Floor Configuration Hook
// ============================================

interface UseProductionFloorConfigResult {
  status: ProductionFloorStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setPin: (pin: string) => Promise<boolean>;
  setEnabled: (enabled: boolean) => Promise<boolean>;
  actionLoading: boolean;
}

export function useProductionFloorConfig(): UseProductionFloorConfigResult {
  const [status, setStatus] = useState<ProductionFloorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getProductionFloorStatus();
      setStatus(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to get production floor status';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const performAction = useCallback(
    async (action: () => Promise<void>, errorMessage: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await action();
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : errorMessage;
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const setPin = useCallback(
    async (pin: string): Promise<boolean> => {
      const action = async () => {
        await setProductionFloorPin(pin);
        // Refetch status to get updated state
        await fetchStatus();
      };
      return performAction(action, 'Failed to set PIN');
    },
    [fetchStatus, performAction]
  );

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      const action = async () => {
        const result = await setProductionFloorEnabled(enabled);
        setStatus((prev) => prev ? { ...prev, isEnabled: result.isEnabled } : null);
      };
      return performAction(action, 'Failed to update status');
    },
    [performAction]
  );

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    setPin,
    setEnabled,
    actionLoading,
  };
}

// ============================================
// Thread Colors Configuration Hook
// ============================================

interface UseThreadColorsResult {
  colors: ThreadColor[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addColor: (color: ThreadColor) => Promise<boolean>;
  removeColor: (colorName: string) => Promise<boolean>;
  updateColors: (colors: ThreadColor[]) => Promise<boolean>;
  actionLoading: boolean;
}

export function useThreadColors(): UseThreadColorsResult {
  const [colors, setColors] = useState<ThreadColor[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchColors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getThreadColors();
      setColors(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch thread colors';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const addColor = useCallback(
    async (color: ThreadColor): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newColors = [...colors, color];
        await updateThreadColors(newColors);
        setColors(newColors);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to add color';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [colors]
  );

  const removeColor = useCallback(
    async (colorName: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newColors = colors.filter((c) => c.name !== colorName);
        await updateThreadColors(newColors);
        setColors(newColors);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove color';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [colors]
  );

  const updateColorsHandler = useCallback(
    async (newColors: ThreadColor[]): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        await updateThreadColors(newColors);
        setColors(newColors);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update colors';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    colors,
    loading,
    error,
    refetch: fetchColors,
    addColor,
    removeColor,
    updateColors: updateColorsHandler,
    actionLoading,
  };
}

// ============================================
// QA Checklist Configuration Hook
// ============================================

interface UseQAChecklistConfigResult {
  config: QAChecklistConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addItem: (item: Omit<QAChecklistItem, 'id' | 'order'>) => Promise<boolean>;
  removeItem: (itemId: string) => Promise<boolean>;
  updateItem: (itemId: string, updates: Partial<Omit<QAChecklistItem, 'id'>>) => Promise<boolean>;
  reorderItems: (items: QAChecklistItem[]) => Promise<boolean>;
  setEnabled: (enabled: boolean) => Promise<boolean>;
  actionLoading: boolean;
}

export function useQAChecklistConfig(): UseQAChecklistConfigResult {
  const [config, setConfig] = useState<QAChecklistConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getQAChecklistConfig();
      setConfig(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch QA checklist';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const addItem = useCallback(
    async (item: Omit<QAChecklistItem, 'id' | 'order'>): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        const newId = `qa-${Date.now()}`;
        const maxOrder = Math.max(0, ...config.checklistItems.map((i) => i.order));
        const newItem: QAChecklistItem = {
          ...item,
          id: newId,
          order: maxOrder + 1,
        };
        const newItems = [...config.checklistItems, newItem];
        await updateQAChecklist(newItems);
        setConfig((prev) => prev ? { ...prev, checklistItems: newItems } : null);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to add item';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const removeItem = useCallback(
    async (itemId: string): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        const newItems = config.checklistItems.filter((i) => i.id !== itemId);
        await updateQAChecklist(newItems);
        setConfig((prev) => prev ? { ...prev, checklistItems: newItems } : null);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove item';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const updateItem = useCallback(
    async (itemId: string, updates: Partial<Omit<QAChecklistItem, 'id'>>): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        const newItems = config.checklistItems.map((i) =>
          i.id === itemId ? { ...i, ...updates } : i
        );
        await updateQAChecklist(newItems);
        setConfig((prev) => prev ? { ...prev, checklistItems: newItems } : null);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update item';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const reorderItems = useCallback(
    async (items: QAChecklistItem[]): Promise<boolean> => {
      if (!config) return false;
      setActionLoading(true);
      setError(null);
      try {
        // Update order based on new array position
        const reorderedItems = items.map((item, index) => ({
          ...item,
          order: index + 1,
        }));
        await updateQAChecklist(reorderedItems);
        setConfig((prev) => prev ? { ...prev, checklistItems: reorderedItems } : null);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to reorder items';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [config]
  );

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const result = await setQAFloorEnabled(enabled);
        setConfig((prev) => prev ? { ...prev, isEnabled: result.isEnabled } : null);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update status';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    config,
    loading,
    error,
    refetch: fetchConfig,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    setEnabled,
    actionLoading,
  };
}

// ============================================
// Size Selection Collections Configuration Hook
// ============================================

interface UseSizeSelectionCollectionsResult {
  collections: SizeSelectionCollection[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addCollection: (collection: SizeSelectionCollection) => Promise<boolean>;
  removeCollection: (collectionId: string) => Promise<boolean>;
  searchResults: SizeSelectionCollection[];
  searchLoading: boolean;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  actionLoading: boolean;
}

export function useSizeSelectionCollections(): UseSizeSelectionCollectionsResult {
  const [collections, setCollections] = useState<SizeSelectionCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SizeSelectionCollection[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSizeSelectionCollections();
      setCollections(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch size selection collections';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCollection = useCallback(
    async (collection: SizeSelectionCollection): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newCollections = [...collections, collection];
        await updateSizeSelectionCollections(newCollections);
        setCollections(newCollections);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to add collection';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [collections]
  );

  const removeCollection = useCallback(
    async (collectionId: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);
      try {
        const newCollections = collections.filter((c) => c.id !== collectionId);
        await updateSizeSelectionCollections(newCollections);
        setCollections(newCollections);
        return true;
      } catch (err) {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove collection';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [collections]
  );

  const search = useCallback(async (query: string): Promise<void> => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const results = await searchCollections(query);
      setSearchResults(results);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Search failed';
      setError(message);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
  }, []);

  return {
    collections,
    loading,
    error,
    refetch: fetchData,
    addCollection,
    removeCollection,
    searchResults,
    searchLoading,
    search,
    clearSearch,
    actionLoading,
  };
}

// ============================================
// Gmail Accounts (Multi-Account) Hook
// ============================================

interface UseGmailAccountsResult {
  accounts: GmailAccount[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  connectAccount: (label: string) => Promise<void>;
  disconnectAccount: (accountId: string) => Promise<boolean>;
  updateAccount: (accountId: string, updates: { label?: string; isActive?: boolean }) => Promise<boolean>;
  actionLoading: boolean;
}

export function useGmailAccounts(): UseGmailAccountsResult {
  const [accounts, setAccounts] = useState<GmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getGmailAccounts();
      setAccounts(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch Gmail accounts';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const connectAccount = useCallback(async (label: string): Promise<void> => {
    setActionLoading(true);
    setError(null);
    try {
      const { authUrl } = await getGmailAccountAuthUrl(label);
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'gmail-account-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type === 'gmail-account-auth-callback' && event.data?.code) {
          window.removeEventListener('message', handleMessage);
          popup?.close();

          try {
            await completeGmailAccountAuth(event.data.code, label);
            await fetchAccounts();
          } catch (err) {
            const message = err instanceof ApiClientError ? err.message : 'Failed to complete Gmail account auth';
            setError(message);
          }
        }
      };

      window.addEventListener('message', handleMessage);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to initiate Gmail account connection';
      setError(message);
    } finally {
      setActionLoading(false);
    }
  }, [fetchAccounts]);

  const disconnectAccount = useCallback(async (accountId: string): Promise<boolean> => {
    setActionLoading(true);
    setError(null);
    try {
      await disconnectGmailAccount(accountId);
      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
      return true;
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to disconnect Gmail account';
      setError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, []);

  const updateAccount = useCallback(async (accountId: string, updates: { label?: string; isActive?: boolean }): Promise<boolean> => {
    setActionLoading(true);
    setError(null);
    try {
      await updateGmailAccount(accountId, updates);
      setAccounts((prev) =>
        prev.map((a) => a.id === accountId ? { ...a, ...updates } : a)
      );
      return true;
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to update Gmail account';
      setError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    accounts,
    loading,
    error,
    refetch: fetchAccounts,
    connectAccount,
    disconnectAccount,
    updateAccount,
    actionLoading,
  };
}
